// pages/CustomerMenuPage.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Lock,
  Minus,
  Plus,
  QrCode,
  Search,
  ShoppingBag,
  Sparkles,
  Store,
  Timer,
  UtensilsCrossed,
  X as XIcon,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { hasLocalSnapshot, loadDashboardBundle } from "@/lib/localDb";
import { MenuShortcutButton } from "@/components/MenuShortcutButton";
import { API, DEFAULT_COVER, formatPrice, getCartKey, http } from "@/app/core";
import { computeSidesPricingForLine, EmptyState, MenuLoadingState, saveCart } from "@/app/fragments";
import { ThemeToggleButton } from "@/app/fullscreen";
import { SideSelectionSheet } from "@/app/menuFragments";

const TABLE_OPTIONS = Array.from({ length: 30 }, (_, index) => `${index + 1}`);
// Fixed category group buckets — order here is the order pills render on the
// customer menu. Owners pick one when creating/editing a category. The "other"
// bucket catches anything unassigned so the menu never loses items.
const CATEGORY_GROUPS = [
  { key: "drinks", label: "Drinks" },
  { key: "starters", label: "Starters" },
  { key: "mains", label: "Mains" },
  { key: "sides", label: "Sides & Extras" },
  { key: "seafood", label: "Seafood" },
  { key: "desserts", label: "Desserts" },
  { key: "specials", label: "Specials" },
  { key: "other", label: "Other" },
];
const CATEGORY_GROUP_LABELS = Object.fromEntries(CATEGORY_GROUPS.map((g) => [g.key, g.label]));

const loadCart = (tagCode) => {
  if (!tagCode) return {};
  try {
    return JSON.parse(window.localStorage.getItem(getCartKey(tagCode)) || "{}");
  } catch {
    return {};
  }
};

const matchesPriceFilter = (item, filterValue) => {
  const price = Number(item.price || 0);
  if (filterValue === "under10") return price < 10;
  if (filterValue === "10to20") return price >= 10 && price <= 20;
  if (filterValue === "over20") return price > 20;
  return true;
};


const sortItems = (items, sortBy) => {
  if (!items || !Array.isArray(items)) return [];
  const sorted = [...items];
  if (sortBy === "price-low") {
    return sorted.sort((a, b) => (Number(a?.price) || 0) - (Number(b?.price) || 0));
  }
  if (sortBy === "price-high") {
    return sorted.sort((a, b) => (Number(b?.price) || 0) - (Number(a?.price) || 0));
  }
  // Default: featured  
  return sorted.sort((a, b) => {
    const aFeatured = Boolean(a?.featured);
    const bFeatured = Boolean(b?.featured);
    if (aFeatured && !bFeatured) return -1;
    if (!aFeatured && bFeatured) return 1;
    return 0;
  });
};

// ---- Cart line modelling (sides-aware) -------------------------------------
// Each cart entry is keyed by a deterministic "line key" so identical
// configurations of (menu item + selected sides) collapse onto a single line.
// Items without sides degrade to lineKey === item_id (matches legacy carts).
const buildCartLineKey = (itemId, sideIds = [], prepSelections = []) => {
  const ids = [...(sideIds || [])].map(String).sort();
  const prep = Array.isArray(prepSelections)
    ? [...prepSelections]
        .map((p) => `${String(p?.label || "")}=${String(p?.choice || "")}`)
        .sort()
        .join("|")
    : "";
  const parts = [String(itemId)];
  if (ids.length) parts.push(`s:${ids.join(",")}`);
  if (prep) parts.push(`p:${prep}`);
  return parts.length === 1 ? parts[0] : parts.join("#");
};

// Old carts were `{ itemId: quantity }`. Walk the loaded payload and upgrade
// any plain-number entries to the new line shape so older browsers don't
// hit a runtime crash after deploy.
const migrateLegacyCart = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const next = {};
  for (const [key, val] of Object.entries(raw)) {
    if (typeof val === "number") {
      // Legacy: key is the item_id and val is the quantity.
      next[String(key)] = { item_id: String(key), quantity: val, selected_side_ids: [], prep_selections: [] };
    } else if (val && typeof val === "object" && Number.isFinite(Number(val.quantity)) && val.item_id) {
      next[String(key)] = {
        item_id: String(val.item_id),
        quantity: Math.max(0, Math.floor(Number(val.quantity))),
        selected_side_ids: Array.isArray(val.selected_side_ids) ? val.selected_side_ids.map(String) : [],
        prep_selections: Array.isArray(val.prep_selections)
          ? val.prep_selections
              .map((p) => ({
                label: String(p?.label || "").trim(),
                choice: String(p?.choice || "").trim(),
              }))
              .filter((p) => p.label && p.choice)
          : [],
      };
    }
  }
  return next;
};

// Module-level cache: dedupes the single-use scan-token redeem across
// React.StrictMode's double-invoked effects (and any other accidental
// double-mount). Key = token. Value = Promise resolving to the redeem
// response. Both effect runs await the same promise, so the backend only
// sees a single redeem call per token.
const __menuRedeemCache = new Map();
function __redeemMenuTokenOnce(token, tagCode) {
  if (!__menuRedeemCache.has(token)) {
    __menuRedeemCache.set(
      token,
      axios
        .post(`${API}/menu/session/redeem`, { token, tag_code: tagCode })
        .then((res) => res.data)
        .catch((err) => {
          // Don't poison the cache for transient errors — let the next
          // mount retry. We only keep successful redemptions cached.
          __menuRedeemCache.delete(token);
          throw err;
        }),
    );
  }
  return __menuRedeemCache.get(token);
}

export function CustomerMenuPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag") || "";
  const [menuData, setMenuData] = useState(null);
  const [cart, setCart] = useState({});
  const [activeCategoryId, setActiveCategoryId] = useState("");
  // Customer-side group filter (Drinks / Mains / Starters / …). Empty string
  // means "show first available group". Auto-resets when the menu changes
  // (handled by a useEffect below).
  const [activeGroup, setActiveGroup] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("featured");
  // Session 27.50 — Full-menu search box.
  //
  // Diner-facing search that filters items across ALL categories/groups
  // by name (and optionally description) as they type. When the search
  // query is non-empty we bypass the group / category filters entirely
  // and render a single flat "Search results" list — because a diner
  // searching for "burger" wants to see every burger regardless of
  // which category it lives in. Category accordions rehydrate as soon
  // as the search is cleared.
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(Boolean(tag));
  const [cartOpen, setCartOpen] = useState(false);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState("");
  const [placingOrder, setPlacingOrder] = useState(false);
  // ── Customer identity + payment method (digital menu) ─────────────
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentStep, setPaymentStep] = useState("cart"); // "cart" | "payment_choice" | "processing"
  // Open the SideSelectionSheet for a given menu item (or null when closed).
  const [sideSheetItem, setSideSheetItem] = useState(null);

  // ---- Call waiter state -----------------------------------------------
  // Cooldown (seconds) after a successful call so repeat taps don't spam
  // the staff dashboard. Backend dedupes too, but the UI countdown gives
  // the customer immediate feedback.
  const [waiterCooldown, setWaiterCooldown] = useState(0);
  const [callingWaiter, setCallingWaiter] = useState(false);
  // Track the most recent call so we can poll for staff acknowledgement
  // and surface a follow-up toast ("Your waiter is on his way.").
  const activeWaiterCallRef = useRef(null);
  useEffect(() => {
    if (waiterCooldown <= 0) return undefined;
    const t = window.setInterval(() => setWaiterCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [waiterCooldown]);

  // ---- Server-enforced menu session ---------------------------------------
  // The backend mints a single-use token whenever someone hits
  // /api/menu/scan?tag=<code> (which is what NFC tags / QR codes redirect
  // through). The frontend redeems that token on first load and stores the
  // resulting session id in sessionStorage (per-tab, so closing the tab
  // kills the session). All order placements include this session id; the
  // backend rejects 401 once the session expires after 30 min idle.
  const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
  // Track the URL-provided scan token explicitly so a fresh scan on an
  // already-locked page (Chrome reuses the same tab) re-triggers redeem
  // even though `tag` hasn't changed.
  const urlSessionToken = searchParams.get("session") || "";
  const sessionStorageKey = tag ? `menu_session_${tag}` : "";
  const [sessionLocked, setSessionLocked] = useState(false);
  const [sessionInitialising, setSessionInitialising] = useState(Boolean(tag));
  const [sessionId, setSessionId] = useState("");
  const sessionIdRef = useRef("");
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Session 31.7 — sessions live in localStorage (was sessionStorage) so
  // closing the tab and coming back keeps the same session. If there is
  // none, or the server no longer knows it, we simply start a new one for
  // the tag — the guest never sees a "rescan" lock screen any more.
  const persistSession = useCallback((id) => {
    if (!sessionStorageKey || typeof window === "undefined") return;
    try {
      if (id) {
        window.localStorage.setItem(sessionStorageKey, JSON.stringify({ session_id: id, last_activity: Date.now() }));
      } else {
        window.localStorage.removeItem(sessionStorageKey);
      }
      try { window.sessionStorage.removeItem(sessionStorageKey); } catch (_e2) { /* legacy key */ }
    } catch (_e) { /* storage full / private mode — ignore */ }
  }, [sessionStorageKey]);

  const readPersistedSession = useCallback(() => {
    if (!sessionStorageKey || typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(sessionStorageKey) || window.sessionStorage.getItem(sessionStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  }, [sessionStorageKey]);

  // Start a brand-new session for this tag (no scan token needed).
  const startFreshSession = useCallback(async () => {
    if (!tag) return "";
    try {
      const r = await axios.post(`${API}/menu/session/start`, { tag_code: tag });
      const id = r.data?.session_id || "";
      if (id) {
        persistSession(id);
        setSessionId(id);
        sessionIdRef.current = id;
        setSessionLocked(false);
      }
      return id;
    } catch (_e) {
      return ""; // tag inactive/invalid → the menu fetch shows its own message
    }
  }, [tag, persistSession]);

  // First-mount: redeem token from URL, or recover existing session.
  // ALSO re-runs whenever a fresh `?session=` token shows up in the URL —
  // this handles the case where a locked user taps the NFC tag again in the
  // same tab. Without this, Chrome reuses the tab, the URL updates, but the
  // effect wouldn't fire because `tag` alone didn't change.
  useEffect(() => {
    if (!tag) return;
    let cancelled = false;
    (async () => {
      setSessionInitialising(true);
      if (urlSessionToken) {
        // Strip the session token out of the URL so it isn't bookmarked.
        try {
          const cleaned = new URL(window.location.href);
          cleaned.searchParams.delete("session");
          window.history.replaceState({}, "", cleaned.toString());
        } catch (_e) { /* noop */ }
        try {
          // __redeemMenuTokenOnce dedupes across React.StrictMode's
          // double-invoked effects so the single-use token is only
          // POSTed once even though this useEffect fires twice in dev.
          const data = await __redeemMenuTokenOnce(urlSessionToken, tag);
          // IMPORTANT: persist BEFORE the cancelled check. Even when the
          // first effect run is cancelled by StrictMode's cleanup, the
          // session_id is the only proof we successfully redeemed the
          // single-use token — the second mount will read it from
          // sessionStorage and recover seamlessly.
          persistSession(data.session_id);
          if (cancelled) return;
          setSessionId(data.session_id);
          // Clear any prior lock state — a fresh rescan always unlocks.
          setSessionLocked(false);
          setSessionInitialising(false);
          return;
        } catch (_err) {
          // Token already used / expired — fall through to check storage.
        }
      }
      // No URL token — reuse the persisted session for this tag.
      const persisted = readPersistedSession();
      if (persisted?.session_id) {
        try {
          await axios.post(`${API}/menu/session/heartbeat`, { session_id: persisted.session_id });
          if (cancelled) return;
          setSessionId(persisted.session_id);
          setSessionLocked(false);
          setSessionInitialising(false);
          return;
        } catch (_err) {
          persistSession("");
        }
      }
      // Session 31.7 — nothing usable: start a fresh session for the tag.
      await startFreshSession();
      if (!cancelled) setSessionInitialising(false);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag, urlSessionToken]);

  // Heartbeat on user activity (throttled) so the 15-min idle timer slides.
  useEffect(() => {
    if (!sessionId || sessionLocked) return undefined;
    let lastBeat = Date.now();
    const beat = async () => {
      const now = Date.now();
      if (now - lastBeat < 60 * 1000) return; // at most once per minute
      lastBeat = now;
      try {
        await axios.post(`${API}/menu/session/heartbeat`, { session_id: sessionIdRef.current });
      } catch (err) {
        // Session 31.7 — server forgot the session → quietly start a new one.
        if (err?.response?.status === 401 || err?.response?.status === 404) {
          persistSession("");
          startFreshSession();
        }
      }
    };
    const events = ["click", "keydown", "touchstart", "pointerdown", "scroll", "wheel"];
    events.forEach((evt) => window.addEventListener(evt, beat, { passive: true }));
    return () => {
      events.forEach((evt) => window.removeEventListener(evt, beat));
    };
  }, [sessionId, sessionLocked, persistSession, startFreshSession]);

  // Send the user back to the scan endpoint so the backend mints a fresh
  // single-use token. This is the ONLY way to get past the lock screen.
  const handleResumeAtVenue = useCallback(() => {
    if (!tag) return;
    persistSession("");
    window.location.href = `${API}/menu/scan?tag=${encodeURIComponent(tag)}`;
  }, [tag, persistSession]);

  const callWaiter = useCallback(async () => {
    if (!sessionId || waiterCooldown > 0 || callingWaiter) return;
    setCallingWaiter(true);
    try {
      const body = { session_id: sessionId };
      if (selectedTable) body.table_number = String(selectedTable);
      const { data } = await axios.post(`${API}/menu/waiter-call`, body);
      toast.success(
        data?.deduped
          ? "Your waiter has already been called — they're on their way."
          : "Your waiter has been called."
      );
      // 60-second cooldown so repeat taps don't flood the dashboard.
      setWaiterCooldown(60);
      // Remember the call id so the poller below can detect when staff
      // acknowledges and fire the follow-up "on his way" toast.
      if (data?.id) {
        activeWaiterCallRef.current = { id: data.id, acked: false };
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reach staff right now. Please try again.");
    } finally {
      setCallingWaiter(false);
    }
  }, [sessionId, waiterCooldown, callingWaiter, selectedTable]);

  // Poll the current call's status while it's outstanding so we can show a
  // follow-up toast the moment a staff member taps "On the way".
  useEffect(() => {
    if (!sessionId) return undefined;
    const tick = async () => {
      const call = activeWaiterCallRef.current;
      if (!call || call.acked) return;
      try {
        const { data } = await axios.get(`${API}/menu/waiter-call/${call.id}`, {
          params: { session_id: sessionId },
        });
        if (data?.status === "acknowledged") {
          call.acked = true;
          activeWaiterCallRef.current = null;
          toast.success("Your waiter is on his way.");
        }
      } catch (_e) { /* network blip — try again next tick */ }
    };
    const id = window.setInterval(tick, 4000);
    return () => window.clearInterval(id);
  }, [sessionId]);

  // Session 27.49 — Use a ref so `fetchMenu` doesn't need `menuData` in
  // its useCallback dependency list. Previously every successful fetch
  // updated `menuData`, which re-created `fetchMenu`, which re-fired
  // the "fetch on mount" AND "60s interval reset" useEffects — kicking
  // off another fetch a few ms later. That infinite loop refreshed the
  // menu every couple seconds and blew away the currently expanded
  // category via `setActiveCategoryId("")` on every response. The
  // customer would tap a category, see the items for two seconds, and
  // watch it collapse itself. See the bug report about menu categories
  // that "auto-close every 2-3 seconds".
  const menuDataRef = useRef(null);
  useEffect(() => { menuDataRef.current = menuData; }, [menuData]);
  const isFirstMenuLoadRef = useRef(true);

  const fetchMenu = useCallback(async (showLoading = true) => {
    if (!tag) return;
    if (showLoading) setLoading(true);
    try {
      const response = await http.get("/menu", { params: { tag } });
      setMenuData(response.data);
      // Only reset the expanded category on the FIRST load. Silent
      // background refreshes must not collapse the diner's currently-
      // open category — that's what caused the 2-3s auto-close.
      if (isFirstMenuLoadRef.current) {
        setActiveCategoryId("");
        isFirstMenuLoadRef.current = false;
      }
      setSelectedTable(response.data.tag?.table_number || "");
    } catch (error) {
      // Session 27.2 — soft-fail on network errors when we already
      // have a local-first paint. This is critical for operator
      // devices viewing the menu on flaky wifi: the previously-primed
      // menu stays on screen instead of vanishing into an error toast.
      const cached = menuDataRef.current;
      const currentlyPrimed = cached && cached.categories && cached.categories.length > 0;
      if (!currentlyPrimed) {
        toast.error(error.response?.data?.detail || "Unable to load this menu.");
        setMenuData(null);
      } // else: keep the local paint, silent background failure.
    } finally {
      setLoading(false);
    }
  }, [tag]);

  // Session 27.2 — LOCAL-FIRST CUSTOMER MENU PAINT.
  //
  // Any device with an installed business snapshot paints the menu
  // instantly from IndexedDB before the /menu network call resolves.
  // Primary beneficiary: operator devices (staff/owner) opening the
  // customer view for order-taking on the floor — they no longer
  // stare at a spinner on flaky wifi.
  //
  // Customers on fresh phones (no PWA install) skip this path
  // entirely — the network fetch is the only source of truth for them.
  const menuLocalPrimedRef = useRef(false);
  useEffect(() => {
    if (!tag) return;
    if (menuLocalPrimedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const primed = await hasLocalSnapshot();
        if (!primed || cancelled) return;
        const bundle = await loadDashboardBundle();
        if (!bundle || cancelled) return;
        // Rebuild the /menu payload shape: business + categories with
        // items attached + specials. Tag block is a best-effort local
        // guess (the server-side response overwrites it with the
        // authoritative tag record — including table_number — as soon
        // as the network fetch lands).
        const cats = (bundle.collections?.categories || [])
          .slice()
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        const items = (bundle.collections?.menu_items || [])
          .filter((it) => it && it.active !== false);
        const itemsByCat = {};
        items.forEach((it) => {
          const cid = it.category_id;
          if (!cid) return;
          (itemsByCat[cid] = itemsByCat[cid] || []).push(it);
        });
        const localMenu = {
          business: bundle.business || null,
          categories: cats.map((c) => ({ ...c, items: itemsByCat[c.id] || [] })),
          specials: (bundle.collections?.specials || []).filter((s) => s && s.active !== false),
          sides: bundle.collections?.sides || [],
          sides_categories: bundle.collections?.sides_categories || [],
          tag: { code: tag, table_number: null, type: "unknown", label: "" },
          requires_table_selection: true,
          _source: "local",
        };
        if (!cancelled && (localMenu.business || localMenu.categories.length)) {
          setMenuData(localMenu);
          setLoading(false);
          menuLocalPrimedRef.current = true;
        }
      } catch (_) { /* silent — local is best-effort */ }
    })();
    return () => { cancelled = true; };
  }, [tag]);

  useEffect(() => {
    // Session 27.51 — Kick off the /menu network fetch AS EARLY AS
    // POSSIBLE. It doesn't require an auth token (the tag alone is
    // sufficient), so we don't need to wait for the session token to
    // redeem before firing it. Previously the flow was strictly
    // sequential — first POST /menu/session/redeem, then GET /menu —
    // which meant customers on NFC scans waited for two round trips
    // before seeing a single menu item. Now the browser fires both
    // requests in parallel and the menu paints as soon as its response
    // lands, independent of whether the session redeem has finished.
    //
    // Ordering is still gated on `sessionId` (see submitOrder), and the
    // LockScreen still overlays if session redemption fails — so this
    // change only affects READ latency, not order-placement safety.
    if (tag && !sessionLocked) {
      // If local primed already, refresh silently in background.
      fetchMenu(!menuLocalPrimedRef.current);
    }
  }, [fetchMenu, tag, sessionLocked]);

  // Re-fetch the menu every 60 seconds so specials activate / deactivate
  // automatically as their schedule windows roll over without forcing the
  // customer to refresh the page.
  useEffect(() => {
    if (!tag || sessionLocked) return undefined;
    // Silent background refresh — no skeleton flash.
    const id = window.setInterval(() => fetchMenu(false), 60 * 1000);
    return () => window.clearInterval(id);
  }, [fetchMenu, tag, sessionLocked]);

  // Session 31.7 — load the saved cart first; only persist AFTER that
  // initial load has landed. Previously the save effect ran on mount with
  // the empty initial state and wiped the stored cart, so "add items,
  // close the tab, come back" always came back empty.
  const [cartHydratedTag, setCartHydratedTag] = useState("");
  useEffect(() => {
    if (tag) {
      setCart(migrateLegacyCart(loadCart(tag)));
      setCartHydratedTag(tag); // batched with setCart → next render saves the LOADED cart
    }
  }, [tag]);

  useEffect(() => {
    if (tag && cartHydratedTag === tag) {
      saveCart(tag, cart);
    }
  }, [cart, tag, cartHydratedTag]);

  const visibleCategories = useMemo(() => {
    const categories = menuData?.categories || [];
    // Session 27.50 — Apply search filter FIRST (across the whole menu),
    // then the price filter, then sort. When the diner types anything
    // into the search box we ignore category `group` filtering
    // downstream so a "burger" search surfaces burgers from every
    // category. The DIY approach lets us search item name AND
    // description without needing a backend index.
    const q = (searchQuery || "").trim().toLowerCase();
    return categories
      .map((category) => ({
        ...category,
        items: sortItems(
          ((category.items || [])
            .filter((item) => item && matchesPriceFilter(item, priceFilter))
            .filter((item) => {
              if (!q) return true;
              const name = String(item.name || "").toLowerCase();
              const desc = String(item.description || "").toLowerCase();
              return name.includes(q) || desc.includes(q);
            })),
          sortBy
        ),
      }))
      .filter((category) => (category.items || []).length > 0);
  }, [menuData?.categories, priceFilter, sortBy, searchQuery]);

  // When searching, expose the whole flattened set of matching items so
  // the UI can render a single "Search results" section that ignores
  // group/category tabs.
  const searchResults = useMemo(() => {
    if (!searchQuery || !searchQuery.trim()) return null;
    return visibleCategories.reduce((acc, c) => acc.concat(c.items || []), []);
  }, [searchQuery, visibleCategories]);

  // Build the ordered list of group keys that actually have items to show.
  // Uses the fixed CATEGORY_GROUPS order so "Drinks" stays first regardless
  // of category sort_order. Categories with no/unknown group fall under
  // "other" (always the rightmost pill) so nothing is ever hidden.
  // The owner can override pill order via Menu Settings → Digital menu
  // section order: "sides_first" promotes the "Sides & Extras" pill to the
  // very front; "items_first" demotes it to the very back (after "other")
  // so the food pills lead the line.
  const availableGroups = useMemo(() => {
    const present = new Set();
    for (const c of visibleCategories) {
      const g = (c.group || "other").toLowerCase();
      present.add(CATEGORY_GROUP_LABELS[g] ? g : "other");
    }
    const ordered = CATEGORY_GROUPS.filter((g) => present.has(g.key));
    const sectionOrder = menuData?.business?.digital_menu_section_order || "items_first";
    const sidesIdx = ordered.findIndex((g) => g.key === "sides");
    if (sidesIdx === -1) return ordered;
    const [sidesGroup] = ordered.splice(sidesIdx, 1);
    if (sectionOrder === "sides_first") {
      ordered.unshift(sidesGroup);
    } else {
      ordered.push(sidesGroup);
    }
    return ordered;
  }, [visibleCategories, menuData?.business?.digital_menu_section_order]);

  // Auto-pick the first available group when the menu loads or the current
  // pick disappears (e.g. last drink got removed). Single-group menus
  // implicitly stay locked to that group.
  useEffect(() => {
    if (!availableGroups.length) {
      setActiveGroup("");
      return;
    }
    if (!activeGroup || !availableGroups.find((g) => g.key === activeGroup)) {
      setActiveGroup(availableGroups[0].key);
    }
  }, [availableGroups, activeGroup]);

  // Categories shown in the grid: no top pill bar anymore — the customer
  // sees every visible category in one scrolling list. Order is controlled
  // entirely by the owner via category sort_order + Menu Settings → Digital
  // menu section order (sides first / items first).
  const gridCategories = visibleCategories;

  useEffect(() => {
    // If the currently-expanded category disappears after a refresh (e.g.
    // owner disabled it), just collapse everything — the customer can pick
    // another category. Don't auto-expand the first one; the accordion
    // starts collapsed so the full list is visible.
    if (activeCategoryId && !visibleCategories.find((category) => category.id === activeCategoryId)) {
      setActiveCategoryId("");
    }
  }, [activeCategoryId, visibleCategories]);

  const itemLookup = useMemo(() => {
    const map = {};
    visibleCategories.forEach((category) => {
      (category.items || []).forEach((item) => {
        map[item.id] = item;
      });
    });
    return map;
  }, [visibleCategories]);

  const cartItems = useMemo(
    () =>
      Object.entries(cart)
        .map(([lineKey, entry]) => {
          // Both legacy (number) and new (object) shapes — be defensive.
          const itemId = entry && typeof entry === "object" ? entry.item_id : lineKey;
          const quantity = entry && typeof entry === "object" ? Number(entry.quantity || 0) : Number(entry || 0);
          if (!itemId || quantity <= 0) return null;
          const selected_side_ids = entry && typeof entry === "object" && Array.isArray(entry.selected_side_ids)
            ? entry.selected_side_ids
            : [];
          const prep_selections = entry && typeof entry === "object" && Array.isArray(entry.prep_selections)
            ? entry.prep_selections
            : [];
          const note = entry && typeof entry === "object" && typeof entry.note === "string" ? entry.note : "";
          const item = itemLookup[itemId] || (menuData?.categories || []).flatMap((category) => category.items || []).find((candidate) => candidate.id === itemId);
          if (!item) return null;
          const sidesPricing = computeSidesPricingForLine(item, selected_side_ids, menuData?.sides_library || []);
          // Per-unit price is item price + paid-side extras (free sides are
          // already counted into free_count).
          const unitPrice = Number(item.price || 0) + sidesPricing.extras_per_unit;
          const vatPercent = Number(item.vat_percent || 0);
          const lineSubtotal = unitPrice * quantity;
          const vatAmount = (lineSubtotal * vatPercent) / 100;
          return {
            ...item,
            lineKey,
            quantity,
            selected_side_ids,
            prep_selections,
            note,
            sidesSnapshots: sidesPricing.snapshots,
            extrasPerUnit: sidesPricing.extras_per_unit,
            lineSubtotal,
            vatAmount,
            lineTotal: lineSubtotal + vatAmount,
          };
        })
        .filter(Boolean),
    [cart, itemLookup, menuData?.categories, menuData?.sides_library]
  );

  const cartCount = useMemo(() => cartItems.reduce((total, item) => total + item.quantity, 0), [cartItems]);
  const cartSubtotal = useMemo(() => cartItems.reduce((total, item) => total + item.lineSubtotal, 0), [cartItems]);
  const cartVat = useMemo(() => cartItems.reduce((total, item) => total + item.vatAmount, 0), [cartItems]);
  const cartTotal = useMemo(() => cartItems.reduce((total, item) => total + item.lineTotal, 0), [cartItems]);

  // Updates a specific cart line keyed by lineKey. For backwards-compat the
  // first argument may also be a plain item_id (no sides) — buildCartLineKey
  // returns the raw id when the side-id list is empty so the callers below
  // can still pass `item.id`.
  const updateItemQuantity = (lineKeyOrItemId, nextQuantity) => {
    // Session 31.7 — outside menu hours you can remove but not add.
    if (menuData?.menu_closed) {
      const cur = cart?.[lineKeyOrItemId];
      const curQty = cur && typeof cur === "object" ? Number(cur.quantity || 0) : 0;
      if (nextQuantity > curQty) { notifyOrderingClosed(); return; }
    }
    setCart((current) => {
      const next = { ...current };
      if (nextQuantity <= 0) {
        delete next[lineKeyOrItemId];
      } else {
        const existing = next[lineKeyOrItemId];
        if (existing && typeof existing === "object") {
          next[lineKeyOrItemId] = { ...existing, quantity: nextQuantity };
        } else {
          // No prior line — treat the key as item_id (no sides).
          next[lineKeyOrItemId] = {
            item_id: String(lineKeyOrItemId),
            quantity: nextQuantity,
            selected_side_ids: [],
            prep_selections: [],
          };
        }
      }
      return next;
    });
  };

  // Add a sides-aware cart line. If a line with identical sides + prep
  // answers already exists, increment it; otherwise create a new line.
  // `addQty` may be a function like prev => prev + 1.
  const notifyOrderingClosed = () => {
    const sched = menuData?.menu_schedule || {};
    const nxt = sched.next_open || (Array.isArray(sched.windows) && sched.windows[0]?.start) || sched.start || "";
    toast.info(nxt ? `Ordering opens at ${nxt}. You can browse the menu until then.` : "Ordering is closed right now. You can still browse the menu.");
  };

  const addCartLine = (item, sideIds = [], addQty = 1, prepSelections = [], note = "") => {
    if (menuData?.menu_closed) { notifyOrderingClosed(); return; }
    if (item && item.sellable === false) { toast.error(`${item.name} is out of stock right now.`); return; }
    const normalisedPrep = Array.isArray(prepSelections)
      ? prepSelections
          .map((p) => ({
            label: String(p?.label || "").trim(),
            choice: String(p?.choice || "").trim(),
          }))
          .filter((p) => p.label && p.choice)
      : [];
    const cleanNote = String(note || "").trim().slice(0, 200);
    // Include note in the line key so two of the "same" item with different
    // notes stay as separate lines on the kitchen ticket.
    const lineKey = buildCartLineKey(item.id, sideIds, normalisedPrep) + (cleanNote ? `|note:${cleanNote}` : "");
    setCart((current) => {
      const next = { ...current };
      const existing = next[lineKey];
      const prevQty = existing && typeof existing === "object" ? Number(existing.quantity || 0) : 0;
      const delta = typeof addQty === "function" ? addQty(prevQty) - prevQty : addQty;
      const newQty = Math.max(0, prevQty + delta);
      if (newQty <= 0) {
        delete next[lineKey];
      } else {
        next[lineKey] = {
          item_id: String(item.id),
          quantity: newQty,
          selected_side_ids: [...(sideIds || [])].map(String),
          prep_selections: normalisedPrep,
          note: cleanNote || "",
        };
      }
      return next;
    });
  };

  const openCheckout = () => {
    if (menuData?.menu_closed) { notifyOrderingClosed(); return; }
    if (!cartItems.length) {
      toast.error("Add something first.");
      return;
    }
    // Validate required customer identity fields.
    if (menuData?.business?.menu_require_name && !customerName.trim()) {
      toast.error("Please enter your name.");
      return;
    }
    if (menuData?.business?.menu_require_phone && !customerPhone.trim()) {
      toast.error("Please enter your phone number.");
      return;
    }
    // If online payments are enabled, show the payment choice step.
    // Session 30.2 — payments are available when the owner enabled menu
    // payments AND at least one gateway is configured (Yoco or an SA
    // gateway in sandbox mode). The backend supplies the ready list.
    const availableGateways = menuData?.business?.payment_gateways || [];
    const paymentsEnabled = menuData?.business?.menu_payments_enabled && availableGateways.length > 0;
    if (paymentsEnabled) {
      setPaymentStep("payment_choice");
      return;
    }
    // Otherwise proceed directly (existing flow).
    if (menuData?.requires_table_selection) {
      setTableDialogOpen(true);
      return;
    }
    submitOrder(menuData?.tag?.table_number || "", null);
  };

  const handlePaymentChoice = async (method) => {
    // method: "cash" | "card" (Yoco) | "payfast" | "ozow" | "snapscan" | "zapper"
    setPaymentStep("processing");
    if (menuData?.requires_table_selection) {
      // Save the chosen method, show table dialog, then submitOrder will use it.
      setPaymentStep("cart");
      setTableDialogOpen(true);
      // We need to pass payment_method into submitOrder. We'll store it temporarily.
      window.__pendingPaymentMethod = method;
      return;
    }
    await submitOrder(menuData?.tag?.table_number || "", method);
  };

  // Session 30.2 — Sandbox payment dialog state for the SA gateways
  // (PayFast / Ozow / SnapScan / Zapper in sandbox mode). The held
  // order is released to the kitchen ONLY on Approve; Cancel deletes
  // it server-side and the cart stays intact for a retry.
  const [sandboxPay, setSandboxPay] = useState(null); // { orderId, gateway, label, amountCents, busy }

  const resolveSandboxPayment = async (outcome) => {
    if (!sandboxPay || sandboxPay.busy) return;
    setSandboxPay((p) => (p ? { ...p, busy: true } : p));
    try {
      await http.post("/checkout/sandbox/confirm", {
        order_id: sandboxPay.orderId,
        gateway: sandboxPay.gateway,
        outcome,
      });
      if (outcome === "approved") {
        setCart({});
        saveCart(tag, {});
        setCartOpen(false);
        const targetId = sandboxPay.orderId;
        setSandboxPay(null);
        toast.success("Payment approved — your order is on its way to the kitchen!");
        navigate(`/order/${targetId}`);
      } else {
        setSandboxPay(null);
        toast.info("Payment cancelled — your cart is still here.");
      }
    } catch (e) {
      setSandboxPay(null);
      toast.error(e.response?.data?.detail || "Payment simulation failed — please try again.");
    }
  };

  const submitOrder = async (tableNumber, paymentMethodOverride, _retry = false) => {
    const paymentMethod = paymentMethodOverride || window.__pendingPaymentMethod || null;
    window.__pendingPaymentMethod = null;
    let activeSession = sessionId || sessionIdRef.current;
    if (!activeSession) {
      // Session 31.7 — no session yet (storage cleared) → start one now.
      activeSession = await startFreshSession();
      if (!activeSession) { toast.error("This menu tag is not active. Please ask a member of staff."); return; }
    }
    setPlacingOrder(true);
    try {
      const response = await http.post("/orders", {
        tag_code: tag,
        table_number: tableNumber || null,
        session_id: activeSession,
        customer_name: customerName.trim() || null,
        customer_phone: customerPhone.trim() || null,
        payment_method: paymentMethod || null,
        items: cartItems.map((item) => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          selected_side_ids: item.selected_side_ids || [],
          prep_selections: item.prep_selections || [],
          note: item.note || "",
        })),
      });
      const orderId = response.data.order.id;

      // If customer chose card payment, create YOCO checkout and redirect.
      if (paymentMethod === "card" || paymentMethod === "yoco") {
        try {
          const checkoutResp = await http.post("/checkout/yoco", {
            order_id: orderId,
            customer_name: customerName.trim() || null,
            customer_phone: customerPhone.trim() || null,
          });
          // Session 30.2 — do NOT clear the cart before redirecting.
          // The order is held server-side until Yoco confirms payment;
          // the success return page clears the cart, and a cancel /
          // failure keeps it intact so the customer can retry.
          setCartOpen(false);
          setTableDialogOpen(false);
          setPaymentStep("cart");
          window.location.assign(checkoutResp.data.redirect_url);
          return;
        } catch (yocoError) {
          // Clean up the held order so nothing lingers server-side.
          try { await http.post(`/orders/${orderId}/abandon`); } catch (_) { /* noop */ }
          toast.error(yocoError.response?.data?.detail || "Unable to create payment — please try again.");
          setPaymentStep("cart");
          setPlacingOrder(false);
          return;
        }
      }

      // Session 30.2 — SA gateways in sandbox mode: in-app simulated
      // approval instead of a redirect. Cart untouched until Approve.
      if (["payfast", "ozow", "snapscan", "zapper"].includes(paymentMethod || "")) {
        try {
          const sb = await http.post("/checkout/sandbox", { order_id: orderId, gateway: paymentMethod });
          setTableDialogOpen(false);
          setPaymentStep("cart");
          setSandboxPay({
            orderId,
            gateway: paymentMethod,
            label: sb.data.gateway_label || paymentMethod,
            amountCents: sb.data.amount_cents || 0,
            busy: false,
          });
          return;
        } catch (sbError) {
          try { await http.post(`/orders/${orderId}/abandon`); } catch (_) { /* noop */ }
          toast.error(sbError.response?.data?.detail || "Unable to start payment — please try again.");
          setPaymentStep("cart");
          setPlacingOrder(false);
          return;
        }
      }

      // Cash flow or no-payment flow: go to order tracking page.
      setCart({});
      saveCart(tag, {});
      setCartOpen(false);
      setTableDialogOpen(false);
      setPaymentStep("cart");
      navigate(`/order/${orderId}`);
    } catch (error) {
      // Session 31.7 — a stale session is not the guest's problem: start a
      // fresh one and retry the order once.
      if (error.response?.status === 401 && !_retry) {
        persistSession("");
        const fresh = await startFreshSession();
        setPlacingOrder(false);
        if (fresh) { window.__pendingPaymentMethod = paymentMethod; return submitOrder(tableNumber, paymentMethod, true); }
        toast.error("This menu tag is not active. Please ask a member of staff.");
      } else if (error.response?.status === 409) {
        // Outside the owner's menu hours — server says when ordering opens.
        toast.error(error.response?.data?.detail || "Ordering is closed right now.");
        fetchMenu();
      } else {
        toast.error(error.response?.data?.detail || "Unable to place order.");
      }
    } finally {
      setPlacingOrder(false);
    }
  };

  if (!tag) {
    return (
      <main className="min-h-screen bg-[radial-gradient(1200px_circle_at_20%_0%,hsl(var(--accent)/0.18),transparent_55%),radial-gradient(900px_circle_at_90%_10%,hsl(var(--primary)/0.14),transparent_50%)] px-4 py-6 sm:px-6 sm:py-10">
        <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
          <ThemeToggleButton />
        </div>
        <div className="mx-auto flex w-full max-w-lg flex-col gap-6">
          <Card className="overflow-hidden rounded-[32px] border-border bg-card shadow-[0_1px_0_rgba(0,0,0,0.04)]">
            <div className="relative h-52 overflow-hidden border-b border-border">
              <img src={DEFAULT_COVER} alt="Restaurant" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(26,31,43,0.45))]" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <Badge className="mb-3 border-0 bg-white/15 text-white">Scan required</Badge>
                <h1 className="font-['Space_Grotesk'] text-3xl font-semibold tracking-tight">Open the menu from a tag.</h1>
              </div>
            </div>
            <CardContent className="space-y-4 p-6">
              <p className="text-sm text-muted-foreground">Scan a menu QR or NFC tag to open the correct restaurant instantly.</p>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  if (sessionLocked) {
    // Diagnostic: capture exactly what arrived in the address bar so we can
    // tell whether the tag's URL is correct (it should have included a
    // ?session=... query string) or whether the tag is still pointing to the
    // legacy /menu?tag=... URL with no token. We collapse this behind a
    // disclosure so it doesn't clutter the customer-facing copy.
    const arrivedUrl = typeof window !== "undefined" ? window.location.href : "";
    const urlObj = (() => { try { return new URL(arrivedUrl); } catch (_e) { return null; } })();
    const hadSessionToken = !!(urlObj && urlObj.searchParams.get("session"));
    const isLegacyTag = !!arrivedUrl && !hadSessionToken;
    return (
      <main className="min-h-screen bg-[radial-gradient(1200px_circle_at_20%_0%,hsl(var(--accent)/0.18),transparent_55%),radial-gradient(900px_circle_at_90%_10%,hsl(var(--primary)/0.14),transparent_50%)] px-4 py-10 sm:px-6">
        <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
          <ThemeToggleButton />
        </div>
        <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-md flex-col items-center justify-center gap-6">
          <Card
            className="w-full overflow-hidden rounded-[32px] border-border bg-card shadow-[0_8px_40px_rgba(0,0,0,0.12)]"
            data-testid="menu-session-locked"
          >
            <CardContent className="space-y-5 p-7 text-center">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]">
                <Lock className="h-9 w-9" />
              </div>
              <div className="space-y-2">
                <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">
                  Session expired
                </h1>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  Your table session has ended. Please tap your phone on the <span className="font-semibold text-foreground">menu NFC tag</span> at your table or scan the <span className="font-semibold text-foreground">QR code</span> on your menu to start a new session.
                </p>
              </div>
              {isLegacyTag ? (
                <div
                  className="rounded-2xl border border-dashed border-[hsl(var(--accent))]/50 bg-[hsl(var(--accent))]/10 p-4 text-left"
                  data-testid="menu-session-legacy-tag-warning"
                >
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
                    NFC tag needs reprogramming
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Your tag opened this page directly, so it skipped the secure scan handshake.
                    Ask the owner to re-write this NFC tag from the Owner dashboard
                    (Tags → Write tag) — the new URL must start with{" "}
                    <span className="font-mono font-semibold text-foreground">/api/menu/scan</span>,
                    not <span className="font-mono">/menu</span>.
                  </p>
                </div>
              ) : null}
              <div className="rounded-2xl border border-dashed border-border bg-secondary/40 p-4 text-left">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Why?</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  This stops anyone reusing the menu URL from their browser history if they aren't physically at the restaurant.
                </p>
              </div>
              <div className="rounded-2xl border border-dashed border-border bg-secondary/40 p-4 text-left">
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">How to continue</p>
                <ol className="mt-2 ml-4 list-decimal space-y-1 text-xs text-muted-foreground">
                  <li><span className="font-semibold text-foreground">Tap your phone</span> on the NFC tag at your table, OR</li>
                  <li><span className="font-semibold text-foreground">Open your camera</span> and scan the QR code on your menu.</li>
                </ol>
                <p className="mt-2 text-xs text-muted-foreground">
                  Refreshing this page or revisiting the URL from history will not unlock it. You must physically rescan the tag at the restaurant.
                </p>
              </div>
              <details className="rounded-2xl border border-dashed border-border bg-secondary/40 p-4 text-left">
                <summary className="cursor-pointer text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  Technical details
                </summary>
                <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                  <p>
                    <span className="font-medium text-foreground">URL opened:</span>{" "}
                    <span className="break-all font-mono">{arrivedUrl || "—"}</span>
                  </p>
                  <p>
                    <span className="font-medium text-foreground">Scan token present:</span>{" "}
                    {hadSessionToken ? "Yes" : "No (this is why the lock fired)"}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    A correctly-programmed tag opens
                    <span className="font-mono"> /api/menu/scan?tag=… </span>
                    which redirects here with a one-time
                    <span className="font-mono"> session </span>
                    parameter.
                  </p>
                </div>
              </details>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto min-h-screen w-full max-w-xl px-4 py-6 sm:px-6">
        <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
          <ThemeToggleButton />
        </div>
        <MenuLoadingState />
      </div>
    );
  }

  if (!menuData) {
    return (
      <div className="mx-auto min-h-screen w-full max-w-xl px-4 py-6 sm:px-6">
        <EmptyState icon={QrCode} title="Menu unavailable" description="This tag is inactive or invalid." />
      </div>
    );
  }

  // Session 31.7 — Menu hours. Outside the owner's service window the
  // guest can still BROWSE the whole menu; only adding to the cart and
  // placing the order are switched off (the server rejects orders too).
  // Replaces the old full-screen "menu not available" page.
  const orderingClosed = Boolean(menuData.menu_closed);
  // Session 47 — view-only menu: cart works, but there is no way to send it.
  const menuViewOnly = menuData?.business?.menu_ordering_enabled === false;
  const closedSched = menuData.menu_schedule || {};
  const closedWindows = Array.isArray(closedSched.windows) && closedSched.windows.length
    ? closedSched.windows
    : [{ start: closedSched.start || "09:00", end: closedSched.end || "22:00" }];
  const nextOpenLabel = closedSched.next_open || closedWindows[0]?.start || "";

  const coverImage = menuData.business?.cover_image || DEFAULT_COVER;

  return (
    <main className="menu-page min-h-screen pb-28">
      <div className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex w-full max-w-xl items-center justify-between gap-4 px-4 py-4 sm:gap-6 sm:px-6">
          <div className="min-w-0 flex-1 pr-1">
            <div className="flex items-center gap-2">
              <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-card text-[hsl(var(--primary))]">
                <Store className="h-4 w-4" />
              </div>
              <p className="min-w-0 flex-1 truncate text-xs font-semibold sm:text-sm" data-testid="customer-table-label">
                {(() => {
                  const label = menuData.tag?.table_number;
                  if (!label) return "Menu";
                  // Numeric table numbers ("2", "12") keep the "Table"
                  // prefix so guests understand the context. Named tags
                  // ("Archie", "Patio 3", "Booth A") skip the prefix
                  // because "Table Archie" reads awkwardly and the extra
                  // word bleeds into the theme + waiter buttons on
                  // narrow phones. `truncate` on this <p> means even a
                  // very long name gets an ellipsis instead of
                  // overflowing.
                  return /^\d+$/.test(String(label).trim()) ? `Table ${label}` : String(label);
                })()}
              </p>
            </div>
          </div>
          <Drawer open={cartOpen} onOpenChange={setCartOpen} shouldScaleBackground={false}>
            <div className="flex items-center gap-2">
              <MenuShortcutButton tag={tag} businessName={menuData.business?.name} />
              <ThemeToggleButton />
              <Button
                variant="outline"
                onClick={callWaiter}
                disabled={!sessionId || callingWaiter || waiterCooldown > 0}
                className="h-11 shrink-0 rounded-2xl border-amber-300 bg-amber-50 px-3 text-amber-900 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200 dark:hover:bg-amber-950/60"
                data-testid="customer-call-waiter-button"
                title={waiterCooldown > 0 ? `Wait ${waiterCooldown}s before calling again` : "Summon a waiter to your table"}
              >
                <Bell className="h-4 w-4" />
                {waiterCooldown > 0 ? `${waiterCooldown}s` : "Call Waiter"}
              </Button>
              <DrawerTrigger asChild>
                <Button variant="outline" className="relative h-11 shrink-0 rounded-2xl bg-card px-4" data-testid="cart-open-button">
                  <ShoppingBag className="h-4 w-4" />
                  {cartCount ? <span className="cart-count">{cartCount}</span> : null}
                </Button>
              </DrawerTrigger>
            </div>
            <DrawerContent className="mx-auto max-h-[88vh] w-full max-w-xl rounded-t-[28px] border-border bg-background pb-[max(env(safe-area-inset-bottom),1rem)]">
              <DrawerHeader className="text-left">
                <DrawerTitle>Your order</DrawerTitle>
                <DrawerDescription>{cartCount} items</DrawerDescription>
              </DrawerHeader>
              <div className="min-h-0 flex-1 overflow-hidden px-4 pb-2">
                <ScrollArea className="h-full">
                  <div className="space-y-3 pr-2">
                    {cartItems.length ? (
                      cartItems.map((item) => (
                        <div key={item.lineKey} className="rounded-2xl border border-border bg-card p-4" data-testid={`cart-line-${item.lineKey}`}>
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="min-w-0 flex-1 truncate font-semibold">{item.name}</p>
                            <p className="shrink-0 whitespace-nowrap font-semibold">{formatPrice(item.lineSubtotal)}</p>
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground">
                            {formatPrice(item.price)}
                            {item.extrasPerUnit > 0 ? ` + ${formatPrice(item.extrasPerUnit)} sides` : ""}
                          </p>
                          {item.sidesSnapshots && item.sidesSnapshots.length ? (
                            <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground" data-testid="cart-line-sides">
                              {item.sidesSnapshots.map((s, idx) => (
                                <li key={`${item.lineKey}-side-${idx}`} className="flex items-center gap-1">
                                  <span aria-hidden>•</span>
                                  <span className="truncate">{s.name}</span>
                                  {s.charged ? (
                                    <span className="ml-1 whitespace-nowrap font-medium text-foreground">+{formatPrice(s.price)}</span>
                                  ) : (
                                    <span className="ml-1 whitespace-nowrap text-emerald-600 dark:text-emerald-400">included</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                          {Array.isArray(item.prep_selections) && item.prep_selections.length ? (
                            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground" data-testid="cart-line-prep">
                              {item.prep_selections.map((p, idx) => (
                                <li key={`${item.lineKey}-prep-${idx}`} className="flex items-start gap-1">
                                  <span aria-hidden>◦</span>
                                  <span className="truncate">
                                    <span className="font-medium text-foreground">{p.label}:</span> {p.choice}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : null}
                          {item.note ? (
                            <p className="mt-1 truncate text-xs italic text-muted-foreground" data-testid="cart-line-note">"{item.note}"</p>
                          ) : null}
                          <div className="mt-4 flex items-center justify-between gap-2">
                            <div className="menu-stepper shrink-0">
                              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => updateItemQuantity(item.lineKey, item.quantity - 1)} aria-label={`Decrease ${item.name}`} data-testid="cart-item-decrease-button">
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="min-w-8 text-center text-sm font-semibold">{item.quantity}</span>
                              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => updateItemQuantity(item.lineKey, item.quantity + 1)} aria-label={`Increase ${item.name}`} data-testid="cart-item-increase-button">
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            <Button type="button" variant="ghost" className="shrink-0 rounded-xl px-3 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => updateItemQuantity(item.lineKey, 0)} data-testid="cart-item-remove-button">
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <EmptyState icon={ShoppingBag} title="Your cart is empty" description="Add items to continue." />
                    )}
                  </div>
                </ScrollArea>
              </div>
              <DrawerFooter>
                <div className="rounded-2xl border border-border bg-secondary/60 p-4 text-sm">
                  <div className="flex items-center justify-between font-semibold text-foreground">
                    <span>Total</span>
                    <span>{formatPrice(cartSubtotal)}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">VAT is added on your receipt.</p>
                  {menuData?.business?.service_fee_enabled && Number(menuData?.business?.service_fee_pct) > 0 ? (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="cart-service-fee-note">
                      A {menuData.business.service_fee_pct}% service fee (about {formatPrice(Math.round(cartSubtotal * Number(menuData.business.service_fee_pct)) / 100)}) is added to your bill. Extra tips are optional.
                    </p>
                  ) : null}
                </div>

                {/* ── Customer identity fields (when enabled) ──── */}
                {(menuData?.business?.menu_require_name || menuData?.business?.menu_require_phone) && (
                  <div className="space-y-2 rounded-2xl border border-border bg-secondary/30 p-3">
                    {menuData?.business?.menu_require_name && (
                      <div>
                        <Label htmlFor="customer-name-input" className="text-xs">Your name</Label>
                        <Input id="customer-name-input" type="text" placeholder="Enter your name" maxLength={120} value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="h-10 rounded-xl" data-testid="customer-name-input" />
                      </div>
                    )}
                    {menuData?.business?.menu_require_phone && (
                      <div>
                        <Label htmlFor="customer-phone-input" className="text-xs">Phone number</Label>
                        <Input id="customer-phone-input" type="tel" placeholder="+27 612 345 678" maxLength={30} value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} className="h-10 rounded-xl" data-testid="customer-phone-input" />
                      </div>
                    )}
                  </div>
                )}

                {menuData?.business?.require_staff_confirmation ? (
                  <div
                    className="flex items-start gap-3 rounded-2xl border border-sky-500/40 bg-sky-500/10 p-3 text-sm text-sky-800 dark:text-sky-200"
                    data-testid="checkout-confirmation-hint"
                    role="note"
                  >
                    <Timer className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-semibold">A waiter will confirm your order</p>
                      <p className="mt-0.5 text-xs leading-relaxed opacity-90">
                        Once you send it through, a member of staff will quickly review it before it heads to the kitchen or bar. This usually only takes a moment.
                      </p>
                    </div>
                  </div>
                ) : null}

                {/* ── Payment choice step ──── */}
                {paymentStep === "payment_choice" ? (
                  <div className="space-y-2" data-testid="payment-choice-section">
                    <p className="text-sm font-semibold text-center">How would you like to pay?</p>
                    {(menuData?.business?.payment_gateways || []).map((gw, idx) => (
                      <Button
                        key={gw.id}
                        variant={idx === 0 ? "default" : "outline"}
                        className={`w-full h-12 rounded-xl gap-2 ${idx === 0 ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" : ""}`}
                        disabled={placingOrder}
                        onClick={() => handlePaymentChoice(gw.id === "yoco" ? "card" : gw.id)}
                        data-testid={`pay-gateway-${gw.id}-btn`}
                      >
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                        {placingOrder ? "Processing…" : gw.label}
                        {gw.mode === "sandbox" ? <Badge variant="secondary" className="ml-1 text-[10px]">Sandbox</Badge> : null}
                      </Button>
                    ))}
                    {(menuData?.business?.menu_cash_allowed !== false) && (
                      <Button variant="outline" className="w-full h-12 rounded-xl gap-2" disabled={placingOrder} onClick={() => handlePaymentChoice("cash")} data-testid="pay-by-cash-btn">
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                        {placingOrder ? "Processing…" : "Pay by cash at counter"}
                      </Button>
                    )}
                    <p className="text-center text-[11px] text-muted-foreground">Your order reaches the kitchen only after payment is approved.</p>
                    <Button variant="ghost" size="sm" className="w-full rounded-xl text-muted-foreground" onClick={() => setPaymentStep("cart")} data-testid="payment-back-btn">Back</Button>
                  </div>
                ) : menuViewOnly ? null : (
                  <Button disabled={!cartItems.length || placingOrder || orderingClosed} className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={openCheckout} data-testid="checkout-place-order-button">
                    {placingOrder ? "Sending..." : orderingClosed ? (nextOpenLabel ? `Ordering opens at ${nextOpenLabel}` : "Ordering closed") : "Place order"}
                  </Button>
                )}
              </DrawerFooter>
            </DrawerContent>
          </Drawer>
        </div>
      </div>

      {orderingClosed ? (
        <div className="mx-auto w-full max-w-xl px-4 pt-4 sm:px-6">
          <div className="flex items-start gap-3 rounded-2xl border border-amber-300/70 bg-amber-50 p-4 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100" role="status" data-testid="customer-menu-closed">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15">
              <Timer className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold">
                {nextOpenLabel ? <>Ordering opens at <span data-testid="customer-menu-next-open">{nextOpenLabel}</span></> : "Ordering is closed right now"}
              </p>
              <p className="mt-0.5 text-sm opacity-90">
                Feel free to browse the menu — items can be added to your order during these hours:{" "}
                {closedWindows.map((w, i) => (
                  <span key={i} className="whitespace-nowrap font-medium tabular-nums">{w.start}–{w.end}{i < closedWindows.length - 1 ? ", " : ""}</span>
                ))}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Session 30.2 — Sandbox payment dialog (PayFast/Ozow/SnapScan/Zapper test mode). */}
      <Dialog
        open={Boolean(sandboxPay)}
        onOpenChange={(open) => {
          if (!open && sandboxPay && !sandboxPay.busy) resolveSandboxPayment("cancelled");
        }}
      >
        <DialogContent className="max-w-sm rounded-2xl" data-testid="sandbox-payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {sandboxPay?.label} payment
              <Badge variant="secondary" className="text-[10px]">Sandbox</Badge>
            </DialogTitle>
            <DialogDescription>
              Simulated checkout for testing — no real money moves. Approve to send the order to the kitchen, or cancel to keep your cart.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-border bg-secondary/40 p-4 text-center">
            <p className="text-xs text-muted-foreground">Amount due</p>
            <p className="text-2xl font-semibold" data-testid="sandbox-payment-amount">
              R{(((sandboxPay?.amountCents) || 0) / 100).toFixed(2)}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Button
              className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              disabled={Boolean(sandboxPay?.busy)}
              onClick={() => resolveSandboxPayment("approved")}
              data-testid="sandbox-approve-btn"
            >
              {sandboxPay?.busy ? "Processing…" : "Approve payment"}
            </Button>
            <Button
              variant="outline"
              className="h-11 rounded-xl"
              disabled={Boolean(sandboxPay?.busy)}
              onClick={() => resolveSandboxPayment("cancelled")}
              data-testid="sandbox-cancel-btn"
            >
              Cancel payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="mx-auto flex w-full max-w-xl flex-col gap-5 px-4 py-5 sm:px-6">
        <Card className="overflow-hidden rounded-[28px] border-border bg-card">
          <div className="relative aspect-[3/1] w-full overflow-hidden border-b border-border">
            <img src={coverImage} alt={menuData.business.name} className={`absolute inset-0 h-full w-full ${menuData.business?.cover_image_fit === "contain" ? "object-contain bg-secondary" : menuData.business?.cover_image_fit === "fill" ? "object-fill" : "object-cover"}`} />
          </div>
          <CardContent className="p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Browse our menu and place your order</p>
            </div>
          </CardContent>
        </Card>

        {/* "LIVE NOW" specials banner removed per owner request — diners can
            still tap the pinned "Today's specials" category to see them. */}

        {/* Session 27.50 — Diner-facing search box.
            Sits above the category grid. Typing anything switches the
            main content to a flat "Search results" list drawn from the
            already-computed `searchResults` memo (which filters items
            by name + description across the entire menu). Clearing the
            box drops back into the normal category-grid experience. */}
        <div className="relative" data-testid="customer-menu-search">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            inputMode="search"
            autoComplete="off"
            placeholder="Search the menu…"
            aria-label="Search the menu"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="h-11 rounded-full border-border bg-background pl-10 pr-10 text-base shadow-sm focus-visible:ring-1 focus-visible:ring-[hsl(var(--primary))]"
            data-testid="customer-menu-search-input"
          />
          {searchQuery ? (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))]"
              aria-label="Clear search"
              data-testid="customer-menu-search-clear"
            >
              <XIcon className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        {/* Category navigation. See surrounding comment. */}
        {searchQuery && searchQuery.trim() ? (
          // Search-results view — a single flat list of matching items,
          // ignoring group/category filters. If no matches we render a
          // helpful empty state.
          searchResults && searchResults.length ? (
            <section
              className="overflow-hidden rounded-[22px] border border-border bg-card"
              data-testid="customer-menu-search-results"
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <p className="text-sm font-medium text-foreground">
                  {searchResults.length}{" "}
                  {searchResults.length === 1 ? "result" : "results"} for “{searchQuery.trim()}”
                </p>
              </div>
              <ul className="divide-y divide-border">
                {searchResults.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setSideSheetItem(item)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:bg-secondary/60"
                      data-testid={`menu-search-item-${item.id}`}
                    >
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt=""
                          className="h-14 w-14 shrink-0 rounded-lg object-cover"
                          loading="lazy"
                        />
                      ) : null}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
                        {item.description ? (
                          <p className="line-clamp-1 text-xs text-muted-foreground">{item.description}</p>
                        ) : null}
                      </div>
                      <p className="shrink-0 text-sm font-semibold text-foreground">
                        {formatPrice(item.price)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <div
              className="rounded-[22px] border border-dashed border-border bg-card px-4 py-8 text-center"
              data-testid="customer-menu-search-empty"
            >
              <p className="text-sm font-medium text-foreground">No matches</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Nothing on the menu matches “{searchQuery.trim()}”. Try a different word or clear the search.
              </p>
            </div>
          )
        ) : visibleCategories.length ? (
          activeCategoryId ? (
            <div className="space-y-3">
              <Button
                variant="outline"
                className="h-10 rounded-full px-4 text-sm"
                onClick={() => setActiveCategoryId("")}
                data-testid="customer-back-to-categories"
              >
                <ChevronLeft className="h-4 w-4" />
                All categories
              </Button>
              {visibleCategories
                .filter((c) => c.id === activeCategoryId)
                .map((category) => {
                  const isOpen = true;
                  const itemCount = (category.items || []).length;
                  const isSpecial = !!category.is_specials_category;
                  return (
                    <section
                      key={category.id}
                      id={`category-${category.id}`}
                      className="overflow-hidden rounded-[22px] border border-[hsl(var(--primary))] bg-card transition-colors"
                      data-testid={`menu-category-${category.id}`}
                    >
                  {/* ---- Header (always visible) ------------------------- */}
                  <button
                    type="button"
                    onClick={() => {
                      if (isOpen) {
                        setActiveCategoryId("");
                      } else {
                        setActiveCategoryId(category.id);
                        // Wait a tick for the DOM to expand, then pull the
                        // header into view so users on long menus don't
                        // have to scroll back up manually.
                        setTimeout(() => {
                          document.getElementById(`category-${category.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 80);
                      }
                    }}
                    className="flex w-full items-center gap-3 px-4 py-4 text-left"
                    aria-expanded={isOpen}
                    aria-controls={`category-panel-${category.id}`}
                    data-testid={`menu-category-toggle-${category.id}`}
                  >
                    {isSpecial ? (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
                        <Sparkles className="h-5 w-5" />
                      </div>
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <h2 className={`font-['Space_Grotesk'] text-lg font-semibold leading-tight ${isSpecial ? "text-[hsl(var(--accent))]" : ""}`}>
                        {category.name}
                      </h2>
                      {category.description ? (
                        <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{category.description}</p>
                      ) : null}
                    </div>
                    <Badge variant="outline" className="shrink-0 border-border bg-background/60 font-normal">
                      {itemCount} {itemCount === 1 ? "item" : "items"}
                    </Badge>
                    <ChevronRight
                      className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`}
                      aria-hidden
                    />
                  </button>

                  {/* ---- Body (shown only when expanded) ---------------- */}
                  {isOpen ? (
                    <div id={`category-panel-${category.id}`} className="space-y-3 border-t border-border px-4 pb-4 pt-4">
                      {/* Per-category filters — moved here from the top
                          so users can refine without leaving the section. */}
                      <div className="flex flex-wrap items-center gap-2">
                        <Select value={priceFilter} onValueChange={setPriceFilter}>
                          <SelectTrigger className="h-9 w-[150px] rounded-xl bg-background text-sm" data-testid={`menu-price-filter-${category.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All prices</SelectItem>
                            <SelectItem value="0-50">R 0 - R 50</SelectItem>
                            <SelectItem value="50-100">R 50 - R 100</SelectItem>
                            <SelectItem value="100-200">R 100 - R 200</SelectItem>
                            <SelectItem value="200+">R 200+</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger className="h-9 w-[170px] rounded-xl bg-background text-sm" data-testid={`menu-sort-${category.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="featured">Featured</SelectItem>
                            <SelectItem value="price-low">Price: Low to High</SelectItem>
                            <SelectItem value="price-high">Price: High to Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-3">
                        {category.items.map((item) => {
                          // Sum all cart lines for this item (a single item can
                          // appear on multiple lines once sides come into play —
                          // e.g. one with chips + one with salad).
                          let totalQuantity = 0;
                          let firstNoSidesLineKey = null;
                          for (const [lk, entry] of Object.entries(cart)) {
                            const e = entry && typeof entry === "object" ? entry : null;
                            if (e && e.item_id === item.id) {
                              totalQuantity += Number(e.quantity || 0);
                              if (!firstNoSidesLineKey && (e.selected_side_ids || []).length === 0) {
                                firstNoSidesLineKey = lk;
                              }
                            } else if (typeof entry === "number" && lk === item.id) {
                              totalQuantity += Number(entry || 0);
                              firstNoSidesLineKey = lk;
                            }
                          }
                          const sidesEnabled = (item.sides_config?.selection_mode || "none") !== "none";
                          const hasPrepPrompts = Array.isArray(item.prep_prompts) && item.prep_prompts.length > 0;
                          // Customer-facing notes are only allowed when the
                          // owner sets notes_mode to "all" (staff-only items
                          // hide the textarea for diners). Route those items
                          // through the sheet too so the customer can add a
                          // note even on simple-by-default items.
                          const customerNotesAllowed = String(item.notes_mode || "off").toLowerCase() === "all";
                          const needsSheet = sidesEnabled || hasPrepPrompts || customerNotesAllowed;
                          // For no-sides items the "add" / stepper still pivots on
                          // the canonical lineKey === item.id so the buttons feel
                          // identical to the pre-sides experience.
                          const stepperKey = firstNoSidesLineKey || item.id;
                          return (
                      <Card key={item.id} className="menu-item-card rounded-[22px] border-border bg-card p-0">
                        <CardContent className="flex items-start gap-4 p-4">
                          {item.image ? (
                            <img src={item.image} alt={item.name} className="item-thumb h-[96px] w-[96px] shrink-0 rounded-2xl object-cover" loading="lazy" />
                          ) : (
                            <div className="flex h-[96px] w-[96px] shrink-0 items-center justify-center rounded-2xl bg-secondary text-[hsl(var(--primary))]">
                              <UtensilsCrossed className="h-8 w-8" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1 space-y-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="space-y-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="text-base font-semibold">{item.name}</h3>
                                  {item.featured ? <Badge className="border-0 bg-emerald-600 text-white hover:bg-emerald-600 dark:bg-emerald-500">Popular</Badge> : null}
                                  {item.out_of_stock ? (
                                    <Badge className={`border-0 ${item.sellable === false ? "bg-destructive text-destructive-foreground hover:bg-destructive" : "bg-amber-500 text-black hover:bg-amber-500"}`} data-testid={`menu-item-oos-badge-${item.id}`}>
                                      Out of stock
                                    </Badge>
                                  ) : null}
                                  {item.is_special ? (
                                    <Badge
                                      className="border-0 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]"
                                      data-testid={`menu-item-special-badge-${item.id}`}
                                    >
                                      Special
                                    </Badge>
                                  ) : null}
                                  {sidesEnabled ? (
                                    <Badge variant="secondary" className="border-border" data-testid={`menu-item-has-sides-${item.id}`}>
                                      Sides
                                    </Badge>
                                  ) : null}
                                  {hasPrepPrompts ? (
                                    <Badge variant="secondary" className="border-border" data-testid={`menu-item-has-prep-${item.id}`}>
                                      Ask how
                                    </Badge>
                                  ) : null}
                                </div>
                                {item.description ? <p className="text-sm text-muted-foreground">{item.description}</p> : null}
                              </div>
                              <div className="text-right">
                                {item.original_price && item.original_price !== item.price ? (
                                  <p className="whitespace-nowrap text-xs text-muted-foreground line-through">{formatPrice(item.original_price)}</p>
                                ) : null}
                                <p className="whitespace-nowrap text-base font-semibold">{formatPrice(item.price)}</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-3">
                              {needsSheet ? (
                                // Items with sides and/or prep prompts go
                                // through the unified options sheet so each
                                // answered combo can land on its own cart
                                // line. Label stays "Add" — the drawer
                                // reveals the questions after tap.
                                <Button
                                  type="button"
                                  className={`h-10 rounded-xl px-4 ${orderingClosed ? "bg-muted text-muted-foreground hover:bg-muted" : "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"}`}
                                  onClick={() => (orderingClosed ? notifyOrderingClosed() : setSideSheetItem(item))}
                                  aria-disabled={orderingClosed}
                                  title={orderingClosed && nextOpenLabel ? `Ordering opens at ${nextOpenLabel}` : undefined}
                                  data-testid="menu-item-choose-sides-button"
                                >
                                  <Plus className="h-4 w-4" />
                                  Add
                                </Button>
                              ) : totalQuantity ? (
                                <div className="menu-stepper">
                                  <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => updateItemQuantity(stepperKey, totalQuantity - 1)} aria-label={`Decrease ${item.name}`} data-testid="menu-item-decrease-button">
                                    <Minus className="h-4 w-4" />
                                  </Button>
                                  <span className="min-w-8 text-center text-sm font-semibold">{totalQuantity}</span>
                                  <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full disabled:opacity-40" onClick={() => updateItemQuantity(stepperKey, totalQuantity + 1)} aria-label={`Increase ${item.name}`} aria-disabled={orderingClosed} data-testid="menu-item-increase-button">
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button type="button" className={`h-10 rounded-xl px-4 ${orderingClosed ? "bg-muted text-muted-foreground hover:bg-muted" : "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"}`} onClick={() => updateItemQuantity(stepperKey, 1)} aria-disabled={orderingClosed} title={orderingClosed && nextOpenLabel ? `Ordering opens at ${nextOpenLabel}` : undefined} data-testid="menu-item-add-button">
                                  <Plus className="h-4 w-4" />
                                  Add
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                        })}
                      </div>
                      {/* Close-category shortcut so users on long menus
                          don't have to scroll back up to collapse. */}
                      <div className="flex justify-center pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          onClick={() => {
                            setActiveCategoryId("");
                            // Scroll the category header back into view so
                            // the user lands on a predictable position.
                            setTimeout(() => {
                              document.getElementById(`category-${category.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }, 60);
                          }}
                          data-testid={`menu-category-close-${category.id}`}
                        >
                          <ChevronUp className="h-4 w-4" />
                          Close category
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </section>
              );
            })}
            </div>
          ) : (
            // Compact grid of category chips — at-a-glance navigation.
            // Specials get the accent treatment so they stand out.
            <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3" data-testid="customer-categories-grid">
              {gridCategories.map((category) => {
                const itemCount = (category.items || []).length;
                const isSpecial = !!category.is_specials_category;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => {
                      setActiveCategoryId(category.id);
                      setTimeout(() => {
                        document.getElementById(`category-${category.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 80);
                    }}
                    className={`flex h-full min-h-[80px] flex-col items-start justify-between gap-1 rounded-2xl border p-3 text-left transition-colors active:scale-[0.98] ${
                      isSpecial
                        ? "border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 hover:bg-[hsl(var(--accent))]/15"
                        : "border-border bg-card hover:bg-secondary/60"
                    }`}
                    data-testid={`customer-category-chip-${category.id}`}
                  >
                    <div className="flex w-full items-start gap-1.5">
                      {isSpecial ? <Sparkles className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--accent))]" aria-hidden /> : null}
                      <span className={`line-clamp-2 text-sm font-semibold leading-tight ${isSpecial ? "text-[hsl(var(--accent))]" : ""}`}>
                        {category.name}
                      </span>
                    </div>
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {itemCount} {itemCount === 1 ? "item" : "items"}
                    </span>
                  </button>
                );
              })}
            </div>
            </div>
          )
        ) : (
          <EmptyState icon={ClipboardList} title="No items match this filter" description="Try a different price range." />
        )}
      </div>

      {cartCount ? (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 p-4 backdrop-blur supports-[backdrop-filter]:bg-background/85">
          <div className="mx-auto flex w-full max-w-xl items-center gap-3 rounded-[24px] border border-border bg-card p-3 shadow-[0_8px_24px_rgba(26,31,43,0.08)]">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-sm font-semibold">{cartCount} items</span>
              <span className="text-sm text-muted-foreground">{formatPrice(cartSubtotal)}</span>
            </div>
            <Button className="h-11 rounded-xl bg-[hsl(var(--accent))] px-5 text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={() => setCartOpen(true)} data-testid="sticky-cart-open-button">
              View cart
            </Button>
          </div>
        </div>
      ) : null}

      <SideSelectionSheet
        item={sideSheetItem}
        sidesLibrary={menuData?.sides_library || []}
        onClose={() => setSideSheetItem(null)}
        onConfirm={(sideIds, prepSelections, note) => {
          if (sideSheetItem) {
            addCartLine(sideSheetItem, sideIds, 1, prepSelections, note);
            toast.success(`Added ${sideSheetItem.name} to your cart.`);
          }
          setSideSheetItem(null);
        }}
      />

      <Dialog open={tableDialogOpen} onOpenChange={setTableDialogOpen}>
        <DialogContent className="rounded-[28px] border-border bg-background sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select your table</DialogTitle>
            <DialogDescription>Choose the table before sending the order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="table-select">Table number</Label>
            <Select value={selectedTable} onValueChange={setSelectedTable}>
              <SelectTrigger id="table-select" className="h-11 rounded-xl bg-card" data-testid="table-select">
                <SelectValue placeholder="Choose your table" />
              </SelectTrigger>
              <SelectContent>
                {TABLE_OPTIONS.map((tableNumber) => (
                  <SelectItem key={tableNumber} value={tableNumber}>
                    Table {tableNumber}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {menuData?.business?.require_staff_confirmation ? (
              <p
                className="mt-1 flex items-start gap-2 rounded-xl bg-sky-500/10 px-3 py-2 text-xs text-sky-800 dark:text-sky-200"
                data-testid="table-select-confirmation-hint"
              >
                <Timer className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>A waiter will quickly confirm your order before it reaches the kitchen or bar.</span>
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-xl" onClick={() => setTableDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={!selectedTable || placingOrder} className="rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90" onClick={() => submitOrder(selectedTable, null)} data-testid="table-select-confirm-button">
              {placingOrder ? "Sending..." : "Confirm and place order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
